import api from './client';

export enum FeedType {
  SEED = 'SEED',
  PELLET = 'PELLET',
  GRAIN = 'GRAIN',
  FRESH = 'FRESH',
  SUPPLEMENT = 'SUPPLEMENT',
  OTHER = 'OTHER'
}

export enum FeedStockUnit {
  KG = 'KG',
  G = 'G',
  L = 'L',
  ML = 'ML'
}

export interface FeedStockNutritionalInfo {
  // Macronutrients (g/kg)
  protein?: number;
  fat?: number;
  fiber?: number;
  carbohydrate?: number;
  moisture?: number;
  calories?: number;
  
  // Major minerals (g/kg)
  calcium?: number;
  phosphorus?: number;
  magnesium?: number;
  potassium?: number;
  sodium?: number;
  
  // Trace minerals (mg/kg)
  iron?: number;
  zinc?: number;
  manganese?: number;
  copper?: number;
  selenium?: number;
  
  // Fat-soluble vitamins
  vitaminA?: number;    // IU/kg
  vitaminD3?: number;   // IU/kg
  vitaminE?: number;    // IU/kg
  vitaminK?: number;    // mg/kg
  
  // Water-soluble vitamins (mg/kg)
  thiamine?: number;     // B1
  riboflavin?: number;   // B2
  niacin?: number;       // B3
  pantothenicAcid?: number; // B5
  pyridoxine?: number;   // B6
  biotin?: number;
  folicAcid?: number;
  vitaminB12?: number;
  choline?: number;
  
  // Analysis metadata
  guaranteed?: string;   // Whether these are guaranteed analysis values
  analysisDate?: string;    // Date of nutritional analysis
  analysisMethod?: string; // Method used for analysis
  certifiedBy?: string;   // Certification authority
  lastUpdated?: string;
}

export interface FeedStock {
  id: string;
  name: string;
  type: FeedType;
  quantity: number;
  unit: FeedStockUnit;
  unitPrice: number;
  totalPrice: number;
  purchaseDate?: string;
  expirationDate?: string;
  supplier?: string;
  batchNumber?: string;
  location?: string;
  notes?: string;
  minimumQuantity: number;
  reorderPoint: number;
  averageDailyUsage: number;
  lastCountQuantity: number;
  lastCountDate?: string;
  lastRestockDate?: string;
  lastRestockQuantity: number;
  restockLeadTimeDays: number;
  nutritionalInfo?: FeedStockNutritionalInfo;
  supplierContact?: string;
  description?: string;
  storageConditions?: string;
  status?: 'in_stock' | 'low_stock' | 'out_of_stock' | 'expired';
  createdAt: string;
  updatedAt: string;
}

export interface CreateFeedStockDto {
  name: string;
  type: FeedType;
  quantity: number;
  unit: FeedStockUnit;
  unitPrice: number;
  purchaseDate?: string;
  expirationDate?: string;
  supplier?: string;
  supplierContact?: string;
  batchNumber?: string;
  location?: string;
  storageConditions?: string;
  description?: string;
  notes?: string;
  minimumQuantity?: number;
  reorderPoint?: number;
  nutritionalInfo?: FeedStockNutritionalInfo;
}

export interface UpdateFeedStockDto extends Partial<CreateFeedStockDto> {}

export interface FeedStockFilters {
  type?: FeedType;
  lowStock?: boolean;
  expiringSoon?: boolean;
  expired?: boolean;
  supplier?: string;
  location?: string;
  unit?: FeedStockUnit;
}

export interface RecordFeedStockUsageDto {
  quantity: number;
  notes?: string;
  usedFor?: string;
}

export interface RecordFeedStockCountDto {
  quantity: number;
  notes?: string;
}

export interface RestockFeedStockDto {
  quantity: number;
  unitPrice?: number;
  supplier?: string;
  batchNumber?: string;
  purchaseDate?: string;
  expirationDate?: string;
  notes?: string;
}

export interface FeedStockTransaction {
  id: string;
  feedStockId: string;
  type: 'purchase' | 'consumption' | 'adjustment' | 'waste';
  quantity: number;
  unit: string;
  date: string;
  reference?: string;
  notes?: string;
  performedBy?: string;
}

export interface UsageTrend {
  period: string;
  dailyAverages: Array<{
    id: string;
    name: string;
    type: string;
    averageDailyUsage: number;
    projectedDaysRemaining: number | null;
  }>;
  monthlyTotals: Array<{
    month: string;
    totalConsumption: number;
    averageDailyConsumption: number;
  }>;
  topConsumed: Array<{
    id: string;
    name: string;
    type: string;
    averageDailyUsage: number;
    currentQuantity: number;
    reorderPoint: number;
    daysUntilReorder: number | null;
  }>;
  summary: {
    totalActiveItems: number;
    averageDailyConsumption: number;
    highestConsumption: number;
    itemsNeedingReorder: number;
  };
}

export interface CostAnalysis {
  period: string;
  totalCost: number;
  totalQuantity: number;
  averageCostPerUnit: number;
  costByType: Record<string, number>;
  costBySupplier: Record<string, number>;
  monthlyTrends: Array<{
    month: string;
    cost: number;
    quantity: number;
    averageUnitCost: number;
  }>;
  inventoryByStockLevel: {
    lowStock: number;
    adequateStock: number;
    overStock: number;
    expired: number;
  };
  summary: {
    totalItems: number;
    averageItemValue: number;
    highestValueItem: number;
    lowestValueItem: number;
  };
}

export interface TrendAnalytics {
  period: string;
  generatedAt: string;
  consumptionTrends: Array<{
    id: string;
    name: string;
    type: string;
    currentUsage: number;
    projections: {
      thirtyDay: number;
      sixtyDay: number;
      ninetyDay: number;
    };
    trendDirection: 'increasing' | 'decreasing' | 'stable';
    velocity: number;
    riskLevel: 'low' | 'medium' | 'high';
    daysToStockout: number | null;
  }>;
  costTrends: {
    monthlyData: Array<{
      month: string;
      totalCost: number;
      totalQuantity: number;
      averageUnitCost: number;
      itemsPurchased: number;
    }>;
    growthRates: {
      cost: number;
      quantity: number;
      unitCost: number;
    };
    volatility: number;
  };
  inventoryTurnover: Array<{
    id: string;
    name: string;
    type: string;
    inventoryValue: number;
    turnoverRatio: number;
    daysOnHand: number;
    efficiency: number;
  }>;
  seasonalPatterns: {
    seasonal: {
      spring: number;
      summer: number;
      autumn: number;
      winter: number;
    };
    monthly: Array<{
      month: number;
      monthName: string;
      usage: number;
      percentage: number;
    }>;
    peakSeason: string;
    volatility: number;
  };
  forecasting: Array<{
    id: string;
    name: string;
    type: string;
    forecasts: Array<{
      date: string;
      projectedQuantity: number;
      confidence: number;
    }>;
    nextRestockDate: string;
    optimumOrderQuantity: number;
  }>;
  alerts: Array<{
    type: string;
    severity: 'low' | 'medium' | 'high';
    itemId?: string;
    itemName?: string;
    message: string;
    recommendation: string;
  }>;
  performanceMetrics: {
    inventoryEfficiency: number;
    costEfficiency: number;
    forecastAccuracy: number;
    totalInventoryValue: number;
    averageTurnover: number;
    optimizationScore: number;
  };
  summary: {
    totalItemsAnalyzed: number;
    trendingUp: number;
    trendingDown: number;
    highVolatility: number;
    stockoutRisk: number;
  };
}

export const feedStockApi = {
  // Get feed stocks (alias for getAll)
  getFeedStocks: (filters?: FeedStockFilters) => 
    api.get<FeedStock[]>('feed-stock', filters ? Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== undefined).map(([key, value]) => [key, String(value)])
    ) : undefined),

  // Basic CRUD operations
  getAll: (filters?: FeedStockFilters) => 
    api.get<FeedStock[]>('feed-stock', filters ? Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== undefined).map(([key, value]) => [key, String(value)])
    ) : undefined),
  
  getById: (id: string) => 
    api.get<FeedStock>(`feed-stock/${id}`),
  
  create: (data: CreateFeedStockDto) => 
    api.post<FeedStock>('feed-stock', data),
  
  update: (id: string, data: UpdateFeedStockDto) => 
    api.patch<FeedStock>(`feed-stock/${id}`, data),
  
  delete: (id: string) => 
    api.delete(`feed-stock/${id}`),

  // AI Nutritional Analysis
  getAINutritionalInfo: (data: {
    feedName: string;
    feedType: FeedType;
    unit: FeedStockUnit;
    description?: string;
  }) => 
    api.post<{
      success: boolean;
      nutritionalInfo?: FeedStockNutritionalInfo;
      confidence: number;
      sources?: string[];
      error?: string;
      validation?: {
        isValid: boolean;
        issues: string[];
        warnings: string[];
      };
    }>('feed-stock/ai-nutritional', data),

  // Transaction operations
  recordUsage: (id: string, data: RecordFeedStockUsageDto) => 
    api.post<FeedStock>(`feed-stock/${id}/usage`, data),
  
  recordCount: (id: string, data: RecordFeedStockCountDto) => 
    api.post<FeedStock>(`feed-stock/${id}/count`, data),
  
  restock: (id: string, data: RestockFeedStockDto) => 
    api.post<FeedStock>(`feed-stock/${id}/restock`, data),

  // History and analytics
  getUsageHistory: (id: string, startDate?: string, endDate?: string) => {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get(`feed-stock/${id}/usage-history`, Object.keys(params).length > 0 ? params : undefined);
  },
  
  getRestockHistory: (id: string, startDate?: string, endDate?: string) => {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get(`feed-stock/${id}/restock-history`, Object.keys(params).length > 0 ? params : undefined);
  },

  // Special queries
  getLowStockItems: () => 
    api.get<FeedStock[]>('feed-stock/low-stock'),
  
  getExpiringSoonItems: () => 
    api.get<FeedStock[]>('feed-stock/expiring-soon'),

  // Analytics
  getUsageTrends: (startDate?: string, endDate?: string) => {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get<UsageTrend>('feed-stock/usage-trends', Object.keys(params).length > 0 ? params : undefined);
  },
  
  getCostAnalysis: (startDate?: string, endDate?: string) => {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get<CostAnalysis>('feed-stock/cost-analysis', Object.keys(params).length > 0 ? params : undefined);
  },

  getTrendAnalytics: (startDate?: string, endDate?: string) => {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return api.get<TrendAnalytics>('feed-stock/trend-analytics', Object.keys(params).length > 0 ? params : undefined);
  },
}; 